import React from "react";
import {
  Box,
  Paper,
  Typography,
  Collapse,
  Chip,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import ReceiptIcon from "@mui/icons-material/Receipt";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PersonIcon from "@mui/icons-material/Person";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import StorefrontIcon from "@mui/icons-material/Storefront";
import InfoCard from "./components/InfoCard";
import ProductsList from "./components/ProductsList";


const UnifiedInvoiceDataDisplay = ({
  invoiceData,
  visible,
  showEanField,
  eanInputs = [],
  onEanInputChange,
  eanValidated = [],
  eanRefs,
  onEanKeyDown,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (!invoiceData) return null;

  const formatCurrency = (value) =>
    `R$ ${value?.toFixed(2).replace(".", ",") || "0,00"}`;

  return (
    <Collapse in={visible} timeout={500}>
      <Paper
        elevation={2}
        sx={{
          p: { xs: 2, md: 3 },
          mb: { xs: 2, md: 3 },
          backgroundColor: "#fff",
          border: "1px solid rgba(255, 152, 0, 0.3)",
          boxShadow: "0 4px 12px rgba(255, 152, 0, 0.15)",
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: { xs: 2, md: 3 },
            flexWrap: "wrap",
            gap: { xs: 1, md: 2 },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ReceiptIcon sx={{ color: "#ff9800" }} />
            <Typography
              variant={{ xs: "subtitle1", md: "h6" }}
              fontWeight={600}
              sx={{
                color: "#ff9800",
                fontSize: { xs: "1rem", md: "1.25rem" },
              }}
            >
              Dados da Nota Fiscal
            </Typography>
          </Box>
          <Chip
            label={invoiceData.channel || "N/A"}
            size={isMobile ? "small" : "medium"}
            sx={{
              backgroundColor: "#ff9800",
              color: "white",
              fontWeight: 600,
              fontSize: { xs: "0.75rem", md: "0.875rem" },
            }}
          />
        </Box>

        <Divider sx={{ mb: { xs: 2, md: 3 } }} />

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: { xs: 2, md: 3 },
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
              },
              gap: { xs: 1.5, md: 2 },
            }}
          >
            <InfoCard
              icon={<ReceiptIcon />}
              title="Número da Nota"
              value={invoiceData.content?.invoice_number}
              color="secondary"
            />
            <InfoCard
              icon={<AttachMoneyIcon />}
              title="Valor da Nota"
              value={formatCurrency(invoiceData.content?.invoice_amount)}
              color="success"
            />
            <InfoCard
              icon={<StorefrontIcon />}
              title="Marketplace"
              value={invoiceData.channel}
              color="info"
            />
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
              },
              gap: { xs: 1.5, md: 2 },
            }}
          >
            <InfoCard
              icon={<LocalShippingIcon />}
              title="Shipping ID"
              value={invoiceData.shipping_id}
              color="warning"
            />

            {invoiceData.shipping && (
              <InfoCard
                icon={<PersonIcon />}
                title="Recebedor"
                value={invoiceData.shipping.receiver_address?.receiver_name}
                color="primary"
              />
            )}
          </Box>

          {invoiceData.shipping && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                },
                gap: { xs: 1.5, md: 2 },
              }}
            >
              <InfoCard
                icon={<ShoppingCartIcon />}
                title="Pedido ID"
                value={invoiceData.shipping.order_id}
                color="secondary"
              />
              <InfoCard
                icon={<AttachMoneyIcon />}
                title="Custo do Pedido"
                value={formatCurrency(invoiceData.shipping.order_cost)}
                color="success"
              />
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
              gap: 2.5,
              mt: { xs: 2, md: 4 },
              p: { xs: 2, md: 3 },
              background: "linear-gradient(90deg, #fff 80%, #fffbe6 100%)",
              borderRadius: 3,
              boxShadow: "0 2px 8px rgba(255, 152, 0, 0.07)",
              border: "1px solid rgba(255, 152, 0, 0.13)",
            }}
          >
            <ProductsList
              invoiceData={invoiceData}
              showEanField={showEanField}
              eanInputs={eanInputs}
              onEanInputChange={onEanInputChange}
              eanValidated={eanValidated}
              eanRefs={eanRefs}
              onEanKeyDown={onEanKeyDown}
            />
          </Box>
        </Box>
      </Paper>
    </Collapse>
  );
};

export default UnifiedInvoiceDataDisplay;